import { useEffect, useState, type ReactElement } from "react";
import { Check, Copy, RotateCcw, Save, Trash2 } from "lucide-react";
import { Badge, Button, FormField, IconButton, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { formatActor } from "../common/formatting";
import { copyToClipboard } from "../common/clipboard";
import { emptyAddress, type Address } from "../common/schemas";
import type { SageOfficeView, SageOfficeUpsertPayload } from "./types";

export type SageOfficeFormMode =
    | { kind: "create" }
    | { kind: "edit"; record: SageOfficeView };

export interface SageOfficeFormProps {
    readonly mode: SageOfficeFormMode;
    readonly onSave: (payload: SageOfficeUpsertPayload) => void;
    readonly onCancel: () => void;
    readonly onRequestDelete: (id: string) => void;
    readonly onRequestRestore: (id: string) => void;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
}

interface FormState {
    readonly id: string;
    readonly name: string;
    readonly address: Address;
}

export function SageOfficeForm({
    mode,
    onSave,
    onCancel,
    onRequestDelete,
    onRequestRestore,
    isSaving,
    errorMessage,
}: SageOfficeFormProps): ReactElement {
    const { isPhone } = useViewport();
    const [state, setState] = useState<FormState>(() => initialState(mode));
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        setState(initialState(mode));
        setValidationError(null);
    }, [mode]);

    function patchAddress(patch: Partial<Address>): void {
        setState((prev) => ({ ...prev, address: { ...prev.address, ...patch } }));
    }

    function handleSubmit(): void {
        const trimmed: SageOfficeUpsertPayload = {
            id: state.id,
            name: state.name.trim(),
            address: {
                street1: state.address.street1.trim(),
                street2:
                    state.address.street2 !== null && state.address.street2 !== undefined
                        ? state.address.street2.trim()
                        : null,
                city: state.address.city.trim(),
                region: state.address.region.trim(),
                postalCode: state.address.postalCode.trim(),
                country: state.address.country.trim().toUpperCase(),
            },
        };

        const error = validate(trimmed);
        if (error !== null) {
            setValidationError(error);
            return;
        }
        setValidationError(null);
        onSave(trimmed);
    }

    const isDeletedRecord = mode.kind === "edit" && mode.record.isDeleted;
    const canEdit = useCanEdit();
    const isReadOnly = isDeletedRecord || !canEdit;
    const auditCreated = mode.kind === "edit" ? mode.record.created : null;
    const auditModified = mode.kind === "edit" ? mode.record.modified : null;
    const auditDeleted = mode.kind === "edit" ? mode.record.deleted : null;

    const actionButtons = (
        <>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
            </Button>
            {canEdit && mode.kind === "edit" && isDeletedRecord ? (
                <Button
                    variant="secondary"
                    leadingIcon={<RotateCcw size={16} />}
                    onClick={() => onRequestRestore(mode.record.id)}
                    disabled={isSaving}
                >
                    Restore
                </Button>
            ) : null}
            {canEdit && mode.kind === "edit" && !isDeletedRecord ? (
                <Button
                    variant="danger"
                    leadingIcon={<Trash2 size={16} />}
                    onClick={() => onRequestDelete(mode.record.id)}
                    disabled={isSaving}
                >
                    Delete
                </Button>
            ) : null}
            {canEdit ? (
                <Button
                    variant="primary"
                    leadingIcon={<Save size={16} />}
                    onClick={handleSubmit}
                    disabled={isSaving || isDeletedRecord}
                >
                    {isSaving ? "Saving…" : "Save"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">
                            {mode.kind === "create" ? "New sage office" : mode.record.name}
                        </Text>
                        {isDeletedRecord ? <Badge tone="danger">Deleted</Badge> : null}
                    </Stack>
                }
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {actionButtons}
                        </Stack>
                    )
                }
            />
            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 720 }}>
                    {validationError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {validationError}
                            </Text>
                        </Surface>
                    ) : null}
                    {errorMessage !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {errorMessage}
                            </Text>
                        </Surface>
                    ) : null}

                    <FormField label="Office name" required>
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.name}
                                onChange={(e) => setState((p) => ({ ...p, name: e.target.value }))}
                                disabled={isReadOnly}
                                placeholder="e.g. Franklin HQ"
                            />
                        )}
                    </FormField>

                    <Text role="subheading">Address</Text>
                    <FormField label="Street 1" required>
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.address.street1}
                                onChange={(e) => patchAddress({ street1: e.target.value })}
                                disabled={isReadOnly}
                            />
                        )}
                    </FormField>
                    <FormField label="Street 2">
                        {(id) => (
                            <TextInput
                                id={id}
                                value={state.address.street2 ?? ""}
                                onChange={(e) => patchAddress({ street2: e.target.value })}
                                disabled={isReadOnly}
                            />
                        )}
                    </FormField>
                    <Stack direction={isPhone ? "column" : "row"} gap={3}>
                        <div style={{ flex: 2 }}>
                            <FormField label="City" required>
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.address.city}
                                        onChange={(e) => patchAddress({ city: e.target.value })}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </div>
                        <div style={{ flex: 1 }}>
                            <FormField label="Region" required>
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.address.region}
                                        onChange={(e) => patchAddress({ region: e.target.value })}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </div>
                    </Stack>
                    <Stack direction={isPhone ? "column" : "row"} gap={3}>
                        <div style={{ flex: 1 }}>
                            <FormField label="Postal code" required>
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.address.postalCode}
                                        onChange={(e) => patchAddress({ postalCode: e.target.value })}
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </div>
                        <div style={{ flex: 1 }}>
                            <FormField label="Country" required hint="ISO-2 (e.g. US, CA)">
                                {(id) => (
                                    <TextInput
                                        id={id}
                                        value={state.address.country}
                                        maxLength={2}
                                        onChange={(e) =>
                                            patchAddress({ country: e.target.value.toUpperCase() })
                                        }
                                        disabled={isReadOnly}
                                    />
                                )}
                            </FormField>
                        </div>
                    </Stack>

                    {mode.kind === "edit" ? (
                        <OfficeIdSection id={mode.record.id} />
                    ) : null}

                    {mode.kind === "edit" ? (
                        <Surface tone="raised" padding={4}>
                            <Stack gap={2}>
                                <Text role="eyebrow">Audit</Text>
                                <Text role="muted">Created — {formatActor(auditCreated)}</Text>
                                <Text role="muted">Modified — {formatActor(auditModified)}</Text>
                                {auditDeleted ? (
                                    <Text role="muted" color={palette.danger}>
                                        Deleted — {formatActor(auditDeleted)}
                                    </Text>
                                ) : null}
                            </Stack>
                        </Surface>
                    ) : null}
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{actionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

function initialState(mode: SageOfficeFormMode): FormState {
    if (mode.kind === "create") {
        return { id: crypto.randomUUID(), name: "", address: { ...emptyAddress } };
    }
    return {
        id: mode.record.id,
        name: mode.record.name,
        address: { ...mode.record.address },
    };
}

function validate(payload: SageOfficeUpsertPayload): string | null {
    if (payload.name === "") return "Name is required.";
    if (payload.address.street1 === "") return "Street 1 is required.";
    if (payload.address.city === "") return "City is required.";
    if (payload.address.region === "") return "Region is required.";
    if (payload.address.postalCode === "") return "Postal code is required.";
    if (payload.address.country.length !== 2) return "Country must be a 2-letter ISO code.";
    return null;
}

/**
 * Surfaces the office's canonical id with a one-click copy. Lives on
 * the detail panel because admins need it for the workspaces bulk-
 * import CSV (the `sageOfficeId` column there is this exact value).
 * Without this section the id was effectively hidden in the SPA — it
 * was never rendered, only used as a route/query key — and operators
 * resorted to inventing UUIDs in their CSVs.
 */
function OfficeIdSection({ id }: { readonly id: string }): ReactElement {
    const [copied, setCopied] = useState(false);

    async function handleCopy(): Promise<void> {
        const ok = await copyToClipboard(id);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    return (
        <Surface tone="raised" padding={4}>
            <Stack gap={2}>
                <Text role="eyebrow">Office id</Text>
                <Stack direction="row" align="center" gap={2} wrap>
                    <code
                        style={{
                            background: palette.surfaceInset,
                            padding: `${space[1]} ${space[2]}`,
                            borderRadius: 4,
                            fontSize: "0.9em",
                        }}
                    >
                        {id}
                    </code>
                    <IconButton
                        tone="neutral"
                        label={copied ? "Copied" : "Copy office id"}
                        icon={copied ? <Check size={14} /> : <Copy size={14} />}
                        onClick={() => {
                            void handleCopy();
                        }}
                    />
                    {copied ? <Text role="muted">Copied</Text> : null}
                </Stack>
                <Text role="muted">
                    Use this in the <code>sageOfficeId</code> column when bulk-importing workspaces.
                </Text>
            </Stack>
        </Surface>
    );
}
